import React from 'react';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="home-page">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img src="/logo.png" alt="Ment-Ally Logo" className="h-32 w-32 drop-shadow-lg" />
          </div>
          
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-4" data-testid="home-title">
            Find Mental Health Support
          </h1>
          <p className="text-2xl font-semibold gradient-text mb-6">
            Across India
          </p>
          <p className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed" data-testid="home-subtitle">
            Connect with verified therapists, counselors, crisis helplines, and mental health services in your area.
            Free, low-cost, and paid options available.
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-12">
            <div className="flex gap-3 shadow-xl rounded-2xl overflow-hidden">
              <input
                type="text"
                placeholder="Search for mental health services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 px-8 py-5 text-lg border-0 focus:ring-0 focus:outline-none"
                data-testid="home-search-input"
                style={{ borderRadius: '16px 0 0 16px' }}
              />
              <button
                type="submit"
                className="btn-gradient px-10 py-5 text-lg font-semibold border-0"
                data-testid="home-search-button"
                style={{ borderRadius: '0 16px 16px 0' }}
              >
                Search
              </button>
            </div>
          </form>

          {/* Quick Actions */}
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => navigate('/search')}
              className="btn-gradient px-8 py-4 text-base"
              data-testid="home-browse-all-button"
            >
              Browse All Services
            </button>
            <button
              onClick={() => navigate('/emergency')}
              className="btn-outline px-8 py-4 text-base"
              data-testid="home-emergency-button"
            >
              📞 Crisis Support Resources
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <div className="card text-center" data-testid="home-feature-verified">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-teal-100 mb-6">
              <span className="text-3xl">✔️</span>
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Verified Services</h3>
            <p className="text-gray-600 leading-relaxed">
              All services are manually reviewed and approved to ensure quality and authenticity.
            </p>
          </div>

          <div className="card text-center" data-testid="home-feature-comprehensive">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-6">
              <span className="text-3xl">📊</span>
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Comprehensive Directory</h3>
            <p className="text-gray-600 leading-relaxed">
              From crisis helplines to therapy centers, find mental health support for every need.
            </p>
          </div>

          <div className="card text-center" data-testid="home-feature-accessible">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-6">
              <span className="text-3xl">🤝</span>
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">Accessible to All</h3>
            <p className="text-gray-600 leading-relaxed">
              Find free, low-cost, and paid services. Filter by language, location, and specialization.
            </p>
          </div>
        </div>
      </div>

      {/* Service Categories */}
      <div className="bg-gradient-to-b from-white to-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">Service Categories</h2>
          <p className="text-center text-gray-600 mb-12 text-lg">Browse services by type</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: 'Crisis Helplines', icon: '📞' },
              { name: 'Free Counselling', icon: '💚' },
              { name: 'Psychiatrists', icon: '🩺' },
              { name: 'Psychologists', icon: '🧑‍⚕️' },
              { name: 'De-addiction Support', icon: '🚫' },
              { name: 'Support Groups', icon: '👥' },
              { name: 'Youth Services', icon: '🧒' },
              { name: 'LGBTQIA+ Support', icon: '🏳️‍🌈' },
            ].map((category, index) => (
              <button
                key={index}
                onClick={() => navigate('/search')}
                className="card hover:shadow-lg p-6 text-left transition-all group"
                data-testid="home-category-card"
              >
                <div className="text-3xl mb-3">{category.icon}</div>
                <span className="font-semibold text-gray-800 group-hover:text-teal-600 transition-colors">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="gradient-teal-blue rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl">
          <h2 className="text-4xl font-bold mb-4" data-testid="home-cta-title">
            Are you a mental health service provider?
          </h2>
          <p className="text-xl mb-10 opacity-90">
            Register your service to help people in need find you.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="bg-white text-teal-600 px-10 py-4 rounded-xl text-lg font-bold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105"
            data-testid="home-cta-button"
          >
            Register Your Service
          </button>
        </div>
      </div>
    </div>
  );
};

export default Home;
